import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TableSortLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import { adminFetch } from './adminApi';
import useTableSort from './useTableSort';

function MayorAdmin() {
  const { t } = useAdminLanguage();
  const [mayors, setMayors] = useState([]);
  const [formData, setFormData] = useState({ 
    nom: '', 
    prenom: '', 
    sexe: '', 
    ff: false, 
    commune: '' 
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    fetchMayors();
  }, [language]);

  const fetchMayors = async () => {
    try {
      const response = await adminFetch(`/api/mayors?lang=${language}`);
      const data = await response.json();
      setMayors(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des bourgmestres');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'ff' ? checked : value 
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.nom.trim() || !formData.prenom.trim() || !formData.sexe || !formData.commune.trim()) {
        setError('Tous les champs sont obligatoires');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      const dataToSend = { ...formData, nom: formData.nom.toUpperCase(), lang: language };

      if (isEditing) {
        await adminFetch(`/api/mayors/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
        setSuccess('Bourgmestre mis à jour avec succès');
      } else {
        await adminFetch('/api/mayors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
        setSuccess('Bourgmestre ajouté avec succès');
      }

      setFormData({ nom: '', prenom: '', sexe: '', ff: false, commune: '' });
      setIsEditing(false);
      setEditingId(null);
      fetchMayors();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du bourgmestre');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (mayor) => {
    setIsEditing(true);
    setEditingId(mayor.id);
    setFormData({ 
      nom: mayor.nom, 
      prenom: mayor.prenom, 
      sexe: mayor.sexe, 
      ff: mayor.ff, 
      commune: mayor.commune 
    });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await adminFetch(`/api/mayors/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Bourgmestre supprimé avec succès');
      fetchMayors();
    } catch (err) {
      setError('Erreur lors de la suppression du bourgmestre');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'nom', label: t('Nom'), sortable: true },
    { id: 'prenom', label: t('Prénom'), sortable: true },
    { id: 'sexe', label: t('Sexe'), sortable: true },
    { id: 'ff', label: 'F.F.', sortable: true },
    { id: 'commune', label: 'Commune', sortable: true },
  ]), [t]);

  const getComparableValue = (mayor, property) => {
    if (property === 'ff') {
      return mayor.ff ? 1 : 0;
    }
    if (property === 'sexe') {
      return mayor.sexe || '';
    }
    return mayor[property]?.toString().toLowerCase() || '';
  };

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(mayors, {
    defaultOrderBy: 'nom',
    getComparableValue,
  });

  const renderCellContent = (mayor, columnId) => {
    switch (columnId) {
      case 'sexe':
        return mayor.sexe;
      case 'ff':
        return mayor.ff ? t('Oui') : t('Non');
      default:
        return mayor[columnId];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des bourgmestres')}
          </Typography>

          <Info type="mayor" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le bourgmestre' : 'Ajouter un bourgmestre')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom"
                  label={t('Nom')}
                  autoComplete="off"
                  value={formData.nom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="prenom"
                  label={t('Prénom')}
                  autoComplete="off"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required disabled={loading}>
                  <InputLabel>{t('Sexe')}</InputLabel>
                  <Select
                    name="sexe"
                    value={formData.sexe}
                    label={t('Sexe')}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="M">{t('Masculin')}</MenuItem>
                    <MenuItem value="F">{t('Féminin')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="commune"
                  label={t('Commune liée')}
                  autoComplete="off"
                  value={formData.commune}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="ff"
                      checked={formData.ff}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  }
                  label="F.F."
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ nom: '', prenom: '', sexe: '', ff: false, commune: '' });
                    }}
                    sx={{ ml: 2 }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des bourgmestres')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((mayor) => (
                    <TableRow key={mayor.id}>
                      {columns.map((column) => (
                        <TableCell key={`${mayor.id}-${column.id}`}>
                          {renderCellContent(mayor, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(mayor)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(mayor.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default MayorAdmin;
